import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { EventsService } from '../../services/events.service';
import { Event } from '../../interfaces/event';
import { Brewery } from '../../interfaces/brewery';
import { BreweriesService } from '../../services/breweries.service';
import { Router, RouterModule } from '@angular/router';
import { EventCardComponent } from '../event-card/event-card.component';
import { BreweryLogoComponent } from "../brewery-logo/brewery-logo.component";
import { ButtonComponent } from '../button/button.component';
import { SkeletonComponent } from '../skeleton/skeleton.component';
import { TrackingService } from '../../services/tracking.service';
import { TranslateModule } from '@ngx-translate/core';
import { EventsShowcaseComponent } from '../events-showcase/events-showcase.component';
import { BannerCarouselBComponent } from '../banner-carousel/banner-carousel-b.component';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterModule, EventCardComponent, BreweryLogoComponent, ButtonComponent, SkeletonComponent, TranslateModule, EventsShowcaseComponent, BannerCarouselBComponent],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {
  events: Event[] = [];
  breweries: Brewery[] = [];
  isLoadingEvents = true;
  isLoadingBreweries = true;
  marqueePaused = false;
  isDragging = false;
  marqueeOffset = 0;

  private touchStartX = 0;
  private dragStartOffset = 0;
  private dragThreshold = 4;
  private marqueeTrackWidth = 0; // width of one set (total / 3)

  constructor(
    private eventsService: EventsService,
    private breweriesService: BreweriesService,
    private router: Router,
    private trackingService: TrackingService
  ) {}

  ngOnInit(): void {
    this.trackingService.track('ViewContent');

    this.eventsService.getEvents().subscribe({
      next: (events) => { this.events = events; this.isLoadingEvents = false; },
      error: () => { this.isLoadingEvents = false; }
    });

    this.breweriesService.getBreweries().subscribe({
      next: (breweries) => { this.breweries = breweries; this.isLoadingBreweries = false; },
      error: () => { this.isLoadingBreweries = false; }
    });
  }

  scrollToSection(sectionId: string) {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  }

  createCustomReservation() {
    this.router.navigate(['new-hop']);
  }

  navigateTo(slug: string) {
    this.router.navigate(['/brewery', slug]);
  }

  onTouchStart(e: TouchEvent) {
    this.isDragging = false;
    this.touchStartX = e.touches[0].clientX;
    this.marqueePaused = true;

    // Capture current animated position and freeze it
    const track = document.querySelector('.partners-marquee') as HTMLElement;
    if (track) {
      const matrix = window.getComputedStyle(track).transform;
      const currentX = matrix && matrix !== 'none'
        ? parseFloat(matrix.split(',')[4])
        : 0;
      this.dragStartOffset = currentX;
      this.marqueeOffset = currentX;
    }
  }

  onTouchMove(e: TouchEvent) {
    const delta = e.touches[0].clientX - this.touchStartX;
    if (Math.abs(delta) > this.dragThreshold) {
      this.isDragging = true;
      this.marqueeOffset = this.dragStartOffset + delta;
    }
  }

  onTouchEnd() {
    if (!this.isDragging) {
      this.marqueePaused = false;
      this.marqueeOffset = 0;
      return;
    }

    const track = document.querySelector('.partners-marquee') as HTMLElement;
    if (track) {
      const oneSetWidth = track.scrollWidth / 6;

      // Normalize into [-oneSetWidth, 0] so the loop stays seamless
      let pos = this.marqueeOffset % oneSetWidth;
      if (pos > 0) pos -= oneSetWidth;

      // Map position to animation-delay: 0 = start, -oneSetWidth = end of cycle
      const progress = Math.abs(pos) / oneSetWidth;
      track.style.animationDelay = `${-(progress * 35)}s`;
      this.marqueeOffset = 0;
    }

    this.isDragging = false;
    this.marqueePaused = false;
  }
}
